import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'vuln-dashboard-preferences'

export interface DashboardPreferences {
  // Theme
  colorMode: 'light' | 'dark' | 'system'

  // Default filters
  defaultExcludeManualNoRisk: boolean
  defaultExcludeAiNoRisk: boolean
  defaultSeverityFilters: string[]

  // Layout
  chartsCollapsed: boolean
  filtersCollapsed: boolean
  insightsCollapsed: boolean
  tableRowDensity: 'compact' | 'normal' | 'comfortable'

  // Features
  showCriticalHighlight: boolean
  showAnalysisInsights: boolean

  // Table columns visibility
  visibleColumns: string[]

  // Search
  recentSearches: string[]
}

const DEFAULT_PREFERENCES: DashboardPreferences = {
  colorMode: 'system',
  defaultExcludeManualNoRisk: false,
  defaultExcludeAiNoRisk: false,
  defaultSeverityFilters: [],
  chartsCollapsed: false,
  filtersCollapsed: false,
  insightsCollapsed: false,
  tableRowDensity: 'normal',
  showCriticalHighlight: true,
  showAnalysisInsights: true,
  visibleColumns: ['severity', 'cve', 'cvss', 'packageName', 'kaiStatus', 'attackVector', 'exploit', 'fixDate', 'groupName', 'imageVersion'],
  recentSearches: [],
}

export function usePreferences() {
  const [preferences, setPreferencesState] = useState<DashboardPreferences>(() => {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) }
      }
    } catch (e) {
      console.warn('Failed to load preferences:', e)
    }
    return DEFAULT_PREFERENCES
  })

  // Persist to localStorage whenever preferences change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
    } catch (e) {
      console.warn('Failed to save preferences:', e)
    }
  }, [preferences])

  const setPreferences = useCallback((
    updates: Partial<DashboardPreferences> | ((prev: DashboardPreferences) => Partial<DashboardPreferences>)
  ) => {
    setPreferencesState(prev => {
      const newUpdates = typeof updates === 'function' ? updates(prev) : updates
      return { ...prev, ...newUpdates }
    })
  }, [])

  const resetPreferences = useCallback(() => {
    setPreferencesState(DEFAULT_PREFERENCES)
  }, [])

  return {
    preferences,
    setPreferences,
    resetPreferences,
  }
}
